import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBuilding, IconInfo } from '../../../icons';
import { isLikelyCorporation } from '../../../types/onboardingTypes';

interface CompanyNameStepProps {
  initialValue: string;
  onSave: (data: { name: string }) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
}

const CompanyNameStep: React.FC<CompanyNameStepProps> = ({
  initialValue,
  onSave,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation('onboarding');
  const [companyName, setCompanyName] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCorporationInfo, setShowCorporationInfo] = useState(false);

  // Prüfe beim Tippen ob es eine Kapitalgesellschaft sein könnte
  useEffect(() => {
    setShowCorporationInfo(isLikelyCorporation(companyName));
  }, [companyName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyName.trim()) {
      setError(t('steps.companyName.errorRequired'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({ name: companyName.trim() });
      onNext();
    } catch (err) {
      setError(t('steps.companyName.errorSaving'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <IconBuilding size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-primary mb-2">
          {t('steps.companyName.title')}
        </h2>
        <p className="text-muted">
          {t('steps.companyName.description')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.companyName.label')} <span className="text-[var(--tone-danger-fg)]">*</span>
          </label>
          <input
            type="text"
            id="companyName"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder={t('steps.companyName.placeholder')}
            className="w-full px-4 py-3 bg-surface-raised border border-strong rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            autoFocus
          />
          
          {/* Hinweis für Kapitalgesellschaften */}
          {showCorporationInfo && (
            <div className="mt-3 p-4 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg">
              <div className="flex items-start">
                <IconInfo size={20} className="text-[var(--tone-info-fg)] mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-[var(--tone-info-fg)] text-sm font-medium">
                    {t('steps.companyName.corporationDetected')}
                  </p>
                  <p className="text-[var(--tone-info-fg)] text-sm mt-1">
                    {t('steps.companyName.corporationInfo')}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <p className="mt-2 text-[var(--tone-danger-fg)] text-sm">{error}</p>
          )}
        </div>

        {/* Examples */}
        <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-4 border border-hairline">
          <p className="text-muted text-sm mb-2">{t('steps.companyName.examples')}</p>
          <ul className="space-y-1 text-faint text-sm">
            <li>• Max Mustermann (Einzelunternehmen)</li>
            <li>• Mustermann IT Solutions GmbH</li>
            <li>• Webdesign Studio Berlin</li>
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 text-muted hover:text-primary transition-colors"
          >
            {t('common.back')}
          </button>
          
          <button
            type="submit"
            disabled={isSaving || !companyName.trim()}
            className="px-8 py-3 bg-indigo-600 text-[#0C1322] font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-indigo-500/30"
          >
            {isSaving ? t('common.saving') : t('common.next')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyNameStep;



